import classnames from "classnames";
import { useEffect, useState } from "react";
import Moment from "react-moment";
import { connect } from "react-redux";
import { Button, Input } from "reactstrap";
import {
  addPackageLeadType,
  deletePackageLeadType,
  editPackageLeadType,
  getPackageLeadTypes,
} from "../../../actions";
import { formValidation } from "../../../function";
import Datatable from "../../Datatable";
import Paginations from "../../Datatable/Paginations";
import Layouts from "../../Layouts";
import DeleteConfirmation from "../../Modals/DeleteConfirmation.js";
import FormConfirmation from "../../Modals/FormConfirmation";
import SideModal from "../../Modals/SideModal";
import AddPackageLeadTypeForm from "./AddPackageLeadTypeForm";
import EditPackageLeadTypeForm from "./EditPackageLeadTypeForm";

const requiredField = {
  package_name: ["required"],
  classification: ["required"],
};

const classificationLabel = (value) => {
  if (value === "lead") return "Lead";
  if (value === "leadless") return "Leadless";
  return "Unknown";
};

const validatePackageLeadType = (data) => {
  const errors = {};

  if (!data.package_name?.trim()) {
    errors.package_name = "Package name is required.";
  } else if (data.package_name.length > 50) {
    errors.package_name = "Package name cannot exceed 50 characters.";
  }

  if (!data.classification) {
    errors.classification = "Classification is required.";
  } else if (!["lead", "leadless", "unknown"].includes(data.classification)) {
    errors.classification = "Invalid classification.";
  }

  return errors;
};

const PackageLeadType = (props) => {
  const [searchText, setSearchText] = useState(null);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [list, setList] = useState([]);
  const [addFormData, setAddFormData] = useState({ classification: "unknown" });
  const [editFormData, setEditFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [formChanged, setFormChanged] = useState(false);
  const [formConfirm, setFormConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const { user } = props;

  useEffect(() => {
    const load = async () => {
      const res = await props.getPackageLeadTypes(props.user.token);
      if (res && res.data) {
        setList(res.data);
      }
    };
    load();
  }, [props]);

  const handleChange = (e) => {
    setSearchText(e.target.value);
    setPageActive(1);
  };

  const handlePopup = (e) => {
    if (formChanged) {
      setFormConfirm(true);
      return false;
    }
    setOpenAddForm(e);
    setErrors({});
  };

  const formConfirmPopup = (confirm) => {
    if (confirm) {
      setFormChanged(false);
      setOpenAddForm(false);
      setOpenEditForm(false);
      setErrors({});
    }
    return setFormConfirm(false);
  };

  const handleEditPopup = () => {
    if (formChanged) {
      setFormConfirm(true);
      return false;
    }
    setOpenEditForm(false);
    setErrors({});
  };

  const handleFormChange = (e) => {
    setFormChanged(true);
    setAddFormData({ ...addFormData, [e.target.name]: e.target.value });
  };

  const handleEditFormChange = (e) => {
    setFormChanged(true);
    setEditFormData({
      ...editFormData,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const postData = {
      ...addFormData,
      package_name: (addFormData.package_name || "").trim().toUpperCase(),
      is_active: true,
    };

    const validationErrors = validatePackageLeadType(postData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const validate = formValidation(requiredField, postData);

    if (validate.formIsValid) {
      const res = await props.addPackageLeadType(props.user.token, postData);
      if (res.code >= 200 && res.code < 400) {
        setList(list.concat(res.data));
        setOpenAddForm(false);
        setAddFormData({ classification: "unknown" });
        setErrors({});
        setFormChanged(false);
      } else {
        setErrors(res.message || {});
      }
    } else {
      setErrors(validate.errors);
    }
  };

  const onUpdate = async (e) => {
    if (e) {
      e.preventDefault();
    }

    const postUpdateData = {
      id: editFormData.id,
      package_name: (editFormData.package_name || "").trim().toUpperCase(),
      classification: editFormData.classification,
      is_active: editFormData.is_active,
    };

    const validationErrors = validatePackageLeadType(postUpdateData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const validate = formValidation(requiredField, postUpdateData);
    if (!validate.formIsValid) {
      setErrors(validate.errors);
      return;
    }

    const res = await props.editPackageLeadType(props.user.token, postUpdateData);
    if (res.code >= 200 && res.code < 400) {
      setList(
        list.map((item) => (res.data.id === item.id ? res.data : item))
      );
      setOpenEditForm(false);
      setErrors({});
      setEditFormData({});
      setFormChanged(false);
    } else {
      setErrors(res.message || {});
    }
  };

  const deleteConfirmPopup = (confirm) => {
    if (confirm) {
      const remove = async () => {
        const res = await props.deletePackageLeadType(
          props.user.token,
          deleteData
        );
        if (res.code >= 200 && res.code < 400) {
          setList(list.filter((item) => item.id !== deleteData.id));
          setDeleteConfirm(false);
          setDeleteData(null);
          setFormChanged(false);
        } else {
          setErrors(res.message || {});
        }
      };
      remove();
    } else {
      setDeleteConfirm(false);
      setDeleteData(null);
    }
  };

  const showEdit = (row) => {
    setOpenEditForm(true);
    setEditFormData(row);
  };

  const showDeleteConfirm = (row) => {
    setDeleteConfirm(true);
    setDeleteData(row);
  };

  const column = [
    {
      fieldName: "package_name",
      label: "Package Name",
      sort: true,
      customRow: (row) => (
        <div className={classnames({ "opacity-50": !row.is_active })}>
          <span>{row.package_name}</span>
        </div>
      ),
    },
    {
      fieldName: "classification_label",
      label: "Classification",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": !row.is_active })}>
          {row.classification_label || classificationLabel(row.classification)}
        </span>
      ),
    },
    {
      fieldName: "created_at",
      label: "Added On",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": !row.is_active })}>
          <Moment format="DD/MM/YYYY">{row.created_at}</Moment>
        </span>
      ),
    },
    {
      fieldName: "",
      label: "Actions",
      width: 80,
      align: "center",
      customRow: (row) =>
        user && user.user.role_id === 1 ? (
          <div className="d-flex justify-content-center">
            <Button size="sm" color="default" onClick={() => showEdit(row)}>
              <span className="fa fa-pencil"></span>
            </Button>
            <Button
              size="sm"
              color="default"
              onClick={() => showDeleteConfirm(row)}
            >
              <span className="fa fa-trash text-danger"></span>
            </Button>
          </div>
        ) : (
          <></>
        ),
    },
  ];

  const [showEntry] = useState(10);
  const [pageActive, setPageActive] = useState(1);
  const [totalRecords, setTotalRecords] = useState(list.length);

  const handlePagination = (e) => {
    setPageActive(e);
  };

  const changeTableParams = (total, page) => {
    if (page) setPageActive(page);
    setTotalRecords(total);
  };

  return (
    <Layouts>
      <SideModal show={openAddForm} close={handlePopup}>
        <AddPackageLeadTypeForm
          errors={errors}
          change={handleFormChange}
          fillData={addFormData}
          submit={onSubmit}
          close={handlePopup}
          requiredField={requiredField}
        />
      </SideModal>

      <SideModal show={openEditForm} close={handleEditPopup}>
        <EditPackageLeadTypeForm
          errors={errors}
          change={handleEditFormChange}
          fillData={editFormData}
          submit={onUpdate}
          close={handleEditPopup}
          requiredField={requiredField}
        />
      </SideModal>

      <FormConfirmation
        show={formConfirm}
        toggle={handlePopup}
        confirm={formConfirmPopup}
      />

      <DeleteConfirmation
        show={deleteConfirm}
        toggle={handlePopup}
        confirm={deleteConfirmPopup}
      />

      <div className="p-20">
        <div className="card">
          <div className="cardHeader">
            <h3>
              Package Lead Types
              <small className="d-block">Total - {list.length}</small>
            </h3>
            <div className="searchBox">
              <Input
                type="text"
                placeholder="Search..."
                name="searchText"
                onChange={(e) => handleChange(e)}
              />
            </div>
            {user && user.user.role_id === 1 && (
              <Button className="linkBtn" onClick={() => handlePopup(true)}>
                <i className="fa fa-plus mr-2"></i>
                Add Package Lead Type
              </Button>
            )}
          </div>
          <Datatable
            data={list}
            columns={column}
            pageActive={pageActive}
            showEntry={showEntry}
            searchText={searchText}
            changeTableParams={changeTableParams}
          />
        </div>
        {totalRecords > showEntry && (
          <Paginations
            totalRecords={totalRecords}
            showEntry={showEntry}
            pageActive={pageActive}
            click={handlePagination}
            prevClick={() => setPageActive(parseInt(pageActive) - 1)}
            nextClick={() => setPageActive(parseInt(pageActive) + 1)}
          />
        )}
      </div>
    </Layouts>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps, {
  getPackageLeadTypes,
  addPackageLeadType,
  editPackageLeadType,
  deletePackageLeadType,
})(PackageLeadType);
